import { Lock, Voicemail } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, EmptyState } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  fieldTypeLabel, greetingFieldName, recordNoun, speakableChoices, speakableKeys, speakableProblem,
  toggleSearchable,
} from "@/lib/forms";
import { useSetSpeakable } from "@/lib/queries";
import type { AdminForm, FormEntity } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The fields a voicemail greeting may say (ADR-113 in `platform`), built as the searchable fields'
 * dialog is: every field of the form with its tick, saved whole.
 *
 * A greeting written as text can say a contact's own fields, the date of their accident for one.
 * These are medical and insurance answers, and what a greeting says is left on a voicemail, so no
 * field is spoken until an admin ticks it here. Only a field with a spoken form can be ticked: a
 * long text, a yes/no, an e-mail, a multi-select and a reference cannot, and the row says so.
 *
 * Unticking works at once. A greeting that still uses the field says its fallback words there, or
 * is not used for that lead, and the voicemail library marks it.
 *
 * The same tick lets a text a cadence sends use the field (ADR-135 in `platform`): a text goes to
 * the lead's phone as a greeting goes to their voicemail, so one answer covers both, and the
 * dialog says so. A text that uses a field unticked later says its fallback words there, or is not
 * sent, and the cadence's page names the field.
 */

/** The platform's own fields a greeting can always say, by the form they belong with. */
const ALWAYS: Record<FormEntity, string[]> = {
  lead: ["First name"],
  firm: ["Name"],
};

export function SpeakableFieldsDialog({
  open, onOpenChange, entity, form, tenantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: FormEntity;
  form: AdminForm | undefined;
  /** A superadmin's tenant; null for everyone else, whose own it is. */
  tenantId: number | null;
}) {
  const toast = useToast();
  const save = useSetSpeakable();
  const [picked, setPicked] = React.useState<string[]>([]);
  const [initial, setInitial] = React.useState<string[]>([]);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const keys = speakableKeys(form);
    setPicked(keys);
    setInitial(keys);
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity]);

  const choices = React.useMemo(() => speakableChoices(form), [form]);
  const changed = JSON.stringify([...picked].sort()) !== JSON.stringify([...initial].sort());
  const problem = speakableProblem(picked, form);
  const noun = recordNoun(entity);
  const unticked = initial.filter((key) => !picked.includes(key));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (problem) {
      setFormError(problem);
      return;
    }
    try {
      await save.mutateAsync({ entity, tenantId, fields: picked });
      toast.success(
        "Spoken fields saved",
        `A greeting can say ${picked.length} ${noun} ${picked.length === 1 ? "field" : "fields"} of your own.`,
      );
      onOpenChange(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save the spoken fields.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Fields a voicemail greeting can say</DialogTitle>
          <DialogDescription>
            {entity === "firm"
              ? "The firm fields a greeting may say to a lead of that firm."
              : "The contact fields a greeting may say to that contact."}{" "}
            A value is left on a voicemail, so tick only what is fine to be heard there. A text a cadence sends can
            use the same fields, and no others.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={(e) => void submit(e)} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            {unticked.length > 0 && (
              <Callout tone="warning">
                A greeting that uses a field you untick stops saying it at once. It says its fallback words
                there, or is not used. The voicemail library marks such a greeting. A cadence&apos;s text that
                uses it does the same, or is not sent.
              </Callout>
            )}

            <div className="space-y-1.5">
              <p className="text-xs font-medium text-text">Always available</p>
              <ul className="divide-y divide-border rounded-md border border-border">
                {ALWAYS[entity].map((name) => (
                  <li key={name} className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted">
                    <Lock className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
                    <span className="flex-1">{name}</span>
                    <span className="text-xs text-text-subtle">One of the greeting's own fields</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p id="speakable-fields-label" className="text-xs font-medium text-text">Your fields</p>
                <span className="text-xs tabular-nums text-text-muted">{picked.length} ticked</span>
              </div>
              {choices.length === 0 ? (
                <EmptyState
                  icon={Voicemail}
                  title="No field to say yet"
                  description={`Create a text, number, phone, date or dropdown ${noun} field first.`}
                />
              ) : (
                <ul role="group" aria-labelledby="speakable-fields-label" className="divide-y divide-border rounded-md border border-border">
                  {choices.map(({ field, section, reason }) => {
                    const on = picked.includes(field.key);
                    // A field that cannot be said is locked off, but never locked on: one the server left
                    // ticked has to be untickable, or nothing can be saved.
                    const locked = Boolean(reason) && !on;
                    const id = `speakable-${field.key}`;
                    return (
                      <li key={field.key} className={cn("flex items-center gap-3 px-3 py-2 text-sm", locked && "text-text-muted")}>
                        <input
                          id={id}
                          type="checkbox"
                          className="accent-accent-9"
                          checked={on}
                          disabled={locked}
                          onChange={(e) => setPicked((keys) => toggleSearchable(keys, field.key, e.target.checked))}
                        />
                        <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer flex-col">
                          <span className={cn("truncate", !locked && "text-text")}>{field.title}</span>
                          <span className="truncate text-xs text-text-subtle">
                            {fieldTypeLabel(field.type)} · {section.title}
                          </span>
                          {on && (
                            // How a greeting writes it, on a line of its own so it is never cut short.
                            <span className="truncate font-mono text-xs text-text-subtle">
                              {`{{${greetingFieldName(entity, field.key)}}}`}
                            </span>
                          )}
                        </label>
                        <span className="shrink-0 text-xs text-text-subtle">{reason ?? ""}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={save.isPending} disabled={!changed}>
              Save
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
